import React, { useState, useEffect } from "react";
import {
  AppstoreOutlined,
  TableOutlined,
  ExpandOutlined,
  FullscreenOutlined,
  DownOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { Dropdown, MenuProps } from "antd";

export const VIEW_MODES = {
  NORMAL: "normal",
  WIDE: "wide",
  WIDER: "wider",
  WIDEST: "widest",
};

const VIEW_MODE_STORAGE_KEY = "app_view_mode";

export const useViewMode = (defaultMode: string = VIEW_MODES.WIDER) => {
  const getResponsiveMode = (currentMode: string | null): string => {
    if (typeof window === "undefined") return currentMode || defaultMode;
    const width = window.innerWidth;
    if (width < 768) return VIEW_MODES.NORMAL;
    if (width < 1024) return VIEW_MODES.WIDE;
    return currentMode || defaultMode;
  };

  const [viewMode, setViewMode] = useState<string>(() => {
    try {
      const stored = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
      const isStoredValid =
        stored && Object.values(VIEW_MODES).includes(stored);
      const initial = isStoredValid ? stored : defaultMode;
      return getResponsiveMode(initial);
    } catch (error) {
      console.error("Error reading view mode from localStorage:", error);
      return getResponsiveMode(defaultMode);
    }
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;

      setViewMode((prevMode) => {
        if (width < 768) {
          if (prevMode !== VIEW_MODES.NORMAL) return VIEW_MODES.NORMAL;
        } else if (width < 1024) {
          if (prevMode !== VIEW_MODES.NORMAL && prevMode !== VIEW_MODES.WIDE) {
            return VIEW_MODES.WIDE;
          }
        }
        return prevMode;
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
    } catch (error) {
      console.error("Error saving view mode to localStorage:", error);
    }
  }, [viewMode]);

  return [viewMode, setViewMode] as const;
};

interface ViewModeSelectProps {
  viewMode: string;
  setViewMode: (mode: string) => void;
}

export const ViewModeSelect = ({ viewMode, setViewMode }: ViewModeSelectProps) => {
  const modeLabels: Record<string, string> = {
    [VIEW_MODES.NORMAL]: "Default",
    [VIEW_MODES.WIDE]: "Wide",
    [VIEW_MODES.WIDER]: "Wider",
    [VIEW_MODES.WIDEST]: "Widest",
  };

  const getIcon = (mode: string) => {
    switch (mode) {
      case VIEW_MODES.WIDE:
        return <TableOutlined style={{ fontSize: "13px" }} />;
      case VIEW_MODES.WIDER:
        return <ExpandOutlined style={{ fontSize: "13px" }} />;
      case VIEW_MODES.WIDEST:
        return <FullscreenOutlined style={{ fontSize: "13px" }} />;
      case VIEW_MODES.NORMAL:
      default:
        return <AppstoreOutlined style={{ fontSize: "13px" }} />;
    }
  };

  const menuItems: MenuProps["items"] = [
    {
      key: VIEW_MODES.NORMAL,
      icon: <AppstoreOutlined style={{ color: "#e67e22" }} />,
      label: (
        <span className="d-flex align-items-center justify-content-between" style={{ minWidth: 155 }}>
          <span>Layout Default (320px)</span>
          {viewMode === VIEW_MODES.NORMAL && <CheckOutlined style={{ fontSize: 11, color: "#2b3034", marginLeft: 8 }} />}
        </span>
      ),
    },
    {
      key: VIEW_MODES.WIDE,
      icon: <TableOutlined style={{ color: "#3498db" }} />,
      label: (
        <span className="d-flex align-items-center justify-content-between" style={{ minWidth: 155 }}>
          <span>Layout Wide (360px)</span>
          {viewMode === VIEW_MODES.WIDE && <CheckOutlined style={{ fontSize: 11, color: "#2b3034", marginLeft: 8 }} />}
        </span>
      ),
    },
    {
      key: VIEW_MODES.WIDER,
      icon: <ExpandOutlined style={{ color: "#9b59b6" }} />,
      label: (
        <span className="d-flex align-items-center justify-content-between" style={{ minWidth: 155 }}>
          <span>Layout Wider (420px)</span>
          {viewMode === VIEW_MODES.WIDER && <CheckOutlined style={{ fontSize: 11, color: "#2b3034", marginLeft: 8 }} />}
        </span>
      ),
    },
    {
      key: VIEW_MODES.WIDEST,
      icon: <FullscreenOutlined style={{ color: "#27ae60" }} />,
      label: (
        <span className="d-flex align-items-center justify-content-between" style={{ minWidth: 155 }}>
          <span>Layout Widest (480px)</span>
          {viewMode === VIEW_MODES.WIDEST && <CheckOutlined style={{ fontSize: 11, color: "#2b3034", marginLeft: 8 }} />}
        </span>
      ),
    },
  ];

  return (
    <Dropdown
      menu={{
        items: menuItems,
        selectedKeys: [viewMode],
        onClick: ({ key }) => setViewMode(key),
      }}
      trigger={["click"]}
      placement="bottom"
    >
      <button
        type="button"
        className="btn d-flex align-items-center gap-2"
        style={{
          backgroundColor: "#ffffff",
          borderColor: "rgba(43, 48, 52, 0.25)",
          borderStyle: "solid",
          borderWidth: "1.5px",
          borderRadius: "50px",
          padding: "7px 16px",
          fontSize: "14px",
          fontWeight: 600,
          color: "#2b3034",
          boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
          transition: "all 0.2s ease",
          cursor: "pointer",
          lineHeight: 1.25,
        }}
      >
        {getIcon(viewMode)}
        <span>{modeLabels[viewMode] || "Layout"}</span>
        <DownOutlined style={{ fontSize: "11px", marginLeft: "2px" }} />
      </button>
    </Dropdown>
  );
};

export const getWrapperStyle = (viewMode: string) => {
  const isNormal = viewMode === VIEW_MODES.NORMAL;
  if (isNormal) return {};
  return {
    width: "100vw",
    position: "relative",
    left: "50%",
    right: "50%",
    marginLeft: "-50vw",
    marginRight: "-50vw",
    display: "flex",
    justifyContent: "center",
  } as React.CSSProperties;
};

export const getInnerStyle = (viewMode: string) => {
  const isNormal = viewMode === VIEW_MODES.NORMAL;
  if (isNormal) return {};
  const widthMap: Record<string, string> = {
    [VIEW_MODES.WIDE]: "1400px",
    [VIEW_MODES.WIDER]: "1600px",
    [VIEW_MODES.WIDEST]: "1800px",
  };
  return {
    width: widthMap[viewMode],
    maxWidth: "100%",
    paddingLeft: "15px",
    paddingRight: "15px",
  } as React.CSSProperties;
};

export const getGapY = (viewMode: string) => {
  switch (viewMode) {
    case VIEW_MODES.WIDE:
      return "80px";
    case VIEW_MODES.WIDER:
      return "100px";
    case VIEW_MODES.WIDEST:
      return "120px";
    case VIEW_MODES.NORMAL:
    default:
      return "50px";
  }
};

